import React from "react";
import { Instagram, TikTok, WhatsApp } from "./Icons";
import { useClinicPicker } from "./ClinicPicker";
import { CLINICS, whatsappUrl } from "../data/clinics";

const QUICK_LINKS = [
  { label: "Facial Treatment", href: "#services" },
  { label: "Anti-Aging", href: "#services" },
  { label: "Face Contouring", href: "#services" },
  { label: "Body Contouring", href: "#services" },
  { label: "Gallery", href: "#gallery" },
];

const SOCIAL_LINKS = [
  {
    icon: Instagram,
    label: "Instagram",
    href: "https://www.instagram.com/marviebeauty_by_dr.winayani",
  },
  {
    icon: TikTok,
    label: "TikTok",
    href: "https://www.tiktok.com/@marviebeautybydrwinayani",
  },
];

const socialIconClass =
  "w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 hover:border-white/20 transition-all duration-200 cursor-pointer";

export default function Footer() {
  const openPicker = useClinicPicker();

  const handleNavClick = (e, href) => {
    if (href.startsWith("#")) {
      e.preventDefault();
      const el = document.querySelector(href);
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({ top, behavior: "smooth" });
      }
    }
  };

  return (
    <footer className="bg-primary text-white">
      {/* CTA Banner */}
      <div className="border-b border-white/10">
        <div className="container-wide mx-auto px-5 sm:px-8 py-16 text-center">
          <h2 className="font-display text-display-md text-white mb-4 text-balance">
            Ready to Start Your <span className="text-accent">Transformation</span>?
          </h2>
          <p className="text-white/60 mb-8 max-w-md mx-auto">
            Book your free consultation today and discover how we can enhance
            your natural beauty.
          </p>
          <button
            onClick={() => openPicker()}
            className="btn bg-accent text-primary px-10 py-4 text-sm font-semibold rounded-2xl
                       hover:bg-accent-300 active:scale-[0.98] transition-all duration-200"
          >
            Book Free Consultation
          </button>
        </div>
      </div>

      {/* Footer Content */}
      <div className="container-wide mx-auto px-5 sm:px-8 py-12">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <img src="/marvie-logo.png" alt="Marvie Beauty" className="h-8 w-auto brightness-0 invert" />
              <span className="font-display text-lg tracking-wide">Marvie Beauty</span>
            </div>
            <p className="text-white/50 text-sm leading-relaxed mb-6 max-w-xs">
              Enhancing natural beauty with safe, personalized aesthetic treatments
              at our two clinics in Denpasar, Bali.
            </p>
            {/* Social */}
            <div className="flex gap-3">
              {SOCIAL_LINKS.map((social) => {
                const IconComponent = social.icon;
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={socialIconClass}
                    aria-label={social.label}
                  >
                    <IconComponent size={18} className="text-white/70" />
                  </a>
                );
              })}
              <button onClick={() => openPicker()} className={socialIconClass} aria-label="WhatsApp">
                <WhatsApp size={18} className="text-white/70" />
              </button>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-sm font-semibold mb-4 text-white/80">Treatments</h3>
            <ul className="space-y-2.5">
              {QUICK_LINKS.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    onClick={(e) => handleNavClick(e, link.href)}
                    className="text-sm text-white/50 hover:text-white transition-colors cursor-pointer"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-sm font-semibold mb-4 text-white/80">Our Clinics</h3>
            <ul className="space-y-5 text-sm text-white/50">
              {CLINICS.map((clinic) => (
                <li key={clinic.id} className="leading-relaxed">
                  <span className="block text-white/80 mb-1">{clinic.name}</span>
                  <a
                    href={clinic.mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block hover:text-white transition-colors cursor-pointer"
                  >
                    {clinic.address}
                  </a>
                  <a
                    href={whatsappUrl(clinic)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-white transition-colors cursor-pointer"
                  >
                    {clinic.phoneDisplay}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Hours */}
          <div>
            <h3 className="text-sm font-semibold mb-4 text-white/80">Opening Hours</h3>
            <ul className="space-y-2.5 text-sm text-white/50">
              {CLINICS.map((clinic) => (
                <li key={clinic.id} className="flex justify-between gap-4">
                  <span>
                    {clinic.area} &middot; {clinic.hours.days}
                  </span>
                  <span className="text-white/70 whitespace-nowrap">{clinic.hours.time}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 p-4 rounded-xl bg-white/5 border border-white/10">
              <p className="text-xs text-white/50">
                Walk-ins welcome. For guaranteed availability, please book in advance via WhatsApp.
              </p>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-xs text-white/40">
            &copy; {new Date().getFullYear()} Marvie Beauty Clinic. All rights reserved.
          </p>
          <p className="text-xs text-white/30">
            Denpasar, Bali &middot; Indonesia
          </p>
        </div>
      </div>
    </footer>
  );
}
